import { Component, ChangeDetectionStrategy, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { PillComponent } from '../pill/pill.component';

export interface TabItem {
  id: string;
  label: string;
  icon?: string;
  count?: number;
}

/**
 * TabBar Component - Horizontal tabs with icons and counts
 *
 * @example
 * <app-atom-tab-bar
 *   [tabs]="tabs"
 *   [activeTab]="activeTab()"
 *   (tabChange)="setActiveTab($event)">
 * </app-atom-tab-bar>
 *
 * // Component:
 * tabs: TabItem[] = [
 *   { id: 'ongoing', label: 'Ongoing', icon: 'control-play', count: 4 },
 *   { id: 'completed', label: 'Completed', icon: 'check', count: 3 },
 *   { id: 'on-hold', label: 'On Hold', icon: 'control-pause', count: 2 }
 * ];
 */
@Component({
  selector: 'app-atom-tab-bar',
  standalone: true,
  imports: [CommonModule, IconComponent, PillComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="border-b border-gray-200">
      <div class="flex">
        @for (tab of tabs(); track tab.id) {
          <button
            type="button"
            (click)="selectTab(tab.id)"
            class="flex-1 flex items-center justify-center gap-2 px-2 py-2 text-sm font-medium transition-colors relative"
            [class.text-blue-600]="activeTab() === tab.id"
            [class.bg-blue-50]="activeTab() === tab.id"
            [class.text-gray-500]="activeTab() !== tab.id"
            [class.hover:text-gray-700]="activeTab() !== tab.id"
            [class.hover:bg-gray-50]="activeTab() !== tab.id"
          >
            @if (tab.icon) {
              <app-atom-icon [name]="tab.icon" size="sm"></app-atom-icon>
            }
            <span>{{ tab.label }}</span>
            @if (tab.count !== undefined) {
              <app-atom-pill [variant]="activeTab() === tab.id ? 'blue' : 'gray'" size="xs">
                {{ tab.count }}
              </app-atom-pill>
            }
            @if (activeTab() === tab.id) {
              <div class="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
            }
          </button>
        }
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class TabBarComponent {
  /** Array of tab items */
  tabs = input.required<TabItem[]>();

  /** Currently active tab ID */
  activeTab = input<string>('');

  /** Emitted when a tab is selected */
  tabChange = output<string>();

  /** Handle tab selection */
  selectTab(tabId: string): void {
    this.tabChange.emit(tabId);
  }
}
